import { MultiSelectPollChoiceData } from '@/hooks/use-multiselect-poll';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import {
    UsercryptCheckinEntry,
    UsercryptChoiceCategory,
} from '@calm/evexia-web-sdk';

export const phqMaxScore = 27;
export const gadMaxScore = 21;

export const phqYAxis = [0, 5, 10, 15, 20, 27];
export const gadYAxis = [0, 5, 10, 15, 21];

export enum CheckinTypeKey {
    Gad = 'gad',
    Phq = 'phq',
    SelfHarm = 'self_harm_thoughts',
    Goal = 'goal',
    Topic = 'topic',
    /* Refinable topic check-in used by the concierge GA guide flow (goals-style refinement tiers) */
    TopicRefinable = 'topic_refinable',
    Emotions = 'emotions',
}

export enum ScreeningResult {
    Asymptomatic = 'asymptomatic',
    SubClinical = 'subclinical',
    Mild = 'mild',
    Moderate = 'moderate',
    ModerateSevere = 'moderatesevere',
    Severe = 'severe',
    Suicidal = 'suicidal',
}

export const screeningResultCutoffs = {
    [ScreeningResult.Asymptomatic]: 1,
    [ScreeningResult.SubClinical]: 5,
    [ScreeningResult.Mild]: 10,
    [ScreeningResult.Moderate]: 15,
    [ScreeningResult.ModerateSevere]: 20,
};

export const screeningResultPrograms = {
    [ScreeningResult.Asymptomatic]: '_hjMA1pH4x',
    [ScreeningResult.SubClinical]: '_hjMA1pH4x',
    [ScreeningResult.Mild]: 'CjYSTS1Lk4',
    [ScreeningResult.Moderate]: '4NtKfFfbed',
    [ScreeningResult.ModerateSevere]: 'QaOMoAmQk4',
    [ScreeningResult.Severe]: 'QaOMoAmQk4',
    [ScreeningResult.Suicidal]: 'KmTbKN7mn1',
};

/** "Preparing for Therapy" - the clinical program surfaced below the survey results. */
export const THERAPY_PROGRAM_ID = 'Hw5jlkv83X';

// Static module-scope map with no theme access: these mode-invariant graph
// colors stay on raw DS_COLOR constants; reading their theme.semantics
// equivalents (surface/graph/screening/*) would require threading a Theme
// through here.
export const screeningResultColors = {
    [ScreeningResult.Asymptomatic]: DS_COLOR.GRAPH_MINIMAL,
    [ScreeningResult.SubClinical]: DS_COLOR.GRAPH_MINIMAL,
    [ScreeningResult.Mild]: DS_COLOR.GRAPH_MILD,
    [ScreeningResult.Moderate]: DS_COLOR.GRAPH_MODERATE,
    [ScreeningResult.ModerateSevere]: DS_COLOR.GRAPH_MODERATE_SEVERE,
    [ScreeningResult.Severe]: DS_COLOR.GRAPH_SEVERE,
    [ScreeningResult.Suicidal]: DS_COLOR.GRAPH_SEVERE,
};

export function classifyResult({
    gadEntry,
    phqEntry,
    selfHarmEntry,
    useModerateSevere = false,
    hideSelfHarmClassification = false,
}: {
    gadEntry?: UsercryptCheckinEntry;
    phqEntry?: UsercryptCheckinEntry;
    selfHarmEntry?: UsercryptCheckinEntry;
    useModerateSevere?: boolean;
    hideSelfHarmClassification?: boolean;
}) {
    if (
        !hideSelfHarmClassification &&
        (selfHarmEntry?.checkins.values[0].value ?? 0) > 0
    ) {
        return ScreeningResult.Suicidal;
    } else {
        const highScore = Math.max(
            gadEntry?.checkins.values[0].value ?? 0,
            phqEntry?.checkins.values[0].value ?? 0
        );

        if (
            highScore >=
                screeningResultCutoffs[ScreeningResult.ModerateSevere] ||
            (!useModerateSevere &&
                highScore >= screeningResultCutoffs[ScreeningResult.Moderate])
        ) {
            return ScreeningResult.Severe;
        } else if (
            useModerateSevere &&
            highScore >= screeningResultCutoffs[ScreeningResult.Moderate]
        ) {
            return ScreeningResult.ModerateSevere;
        } else if (highScore >= screeningResultCutoffs[ScreeningResult.Mild]) {
            return ScreeningResult.Moderate;
        } else if (
            highScore >= screeningResultCutoffs[ScreeningResult.SubClinical]
        ) {
            return ScreeningResult.Mild;
        } else if (
            highScore >= screeningResultCutoffs[ScreeningResult.Asymptomatic]
        ) {
            return ScreeningResult.SubClinical;
        } else {
            return ScreeningResult.Asymptomatic;
        }
    }
}

export function sortCheckins(
    a: UsercryptCheckinEntry,
    b: UsercryptCheckinEntry
) {
    return (
        new Date(a.checkinDate ?? '').getTime() -
        new Date(b.checkinDate || '').getTime()
    );
}

export function getSortedUniqueCategories(
    topicChoices: MultiSelectPollChoiceData[]
) {
    if (!topicChoices?.length) return [];
    const uniqueCategories = topicChoices.reduce(
        (acc: UsercryptChoiceCategory[], topic) => {
            const category = topic.category;
            if (typeof category === 'undefined') return acc;
            const categoryName = category.name?.toLocaleLowerCase();
            if (
                categoryName &&
                !acc.some((c) => c.name?.toLocaleLowerCase() === categoryName)
            ) {
                acc.push(category);
            }
            return acc;
        },
        []
    );
    return uniqueCategories.sort((a, b) => {
        const aOrder = typeof a.order === 'number' ? a.order : Infinity;
        const bOrder = typeof b.order === 'number' ? b.order : Infinity;
        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }
        if (a.name && b.name) {
            return a.name.localeCompare(b.name);
        }
        return 0;
    });
}
